import Box from "@mui/material/Box";
import Paper, {type PaperProps} from "@mui/material/Paper";
import {alpha, styled} from "@mui/material/styles";
import type {Theme} from "@mui/material/styles";
import type {ElementType, ReactElement} from "react";

import {projectCardClasses as classes} from "./classes.js";

export const PROJECT_CARD_WIDE_THRESHOLD = 500;
export const PROJECT_CARD_ROW_GAP_PX = 12;
export const UNIFORM_PROJECT_CARD_HEIGHT_PX = 181;
export const UNIFORM_PROJECT_CARD_ROW_HEIGHT_PX = UNIFORM_PROJECT_CARD_HEIGHT_PX + PROJECT_CARD_ROW_GAP_PX;
export const UNIFORM_PROJECT_CARD_TITLE_HEIGHT_PX = 26;

const PROJECT_CARD_PREVIOUS_SIBLING_SELECTOR = `.${classes.cardWrapper} + &, .${classes.cardContainer} + &`;
const UNIFORM_DESCRIPTION_HEIGHT_PX = 44;
const UNIFORM_AUTHOR_HEIGHT_PX = 26;
const UNIFORM_METRIC_HEIGHT_PX = 33;

interface ProjectCardRootStyleProps {
    widthResponsive?: boolean;
    uniformHeight?: boolean;
    titleExpanded?: boolean;
    descriptionExpanded?: boolean;
}

type ProjectCardRootComponent = (
    props: PaperProps & ProjectCardRootStyleProps & {component?: ElementType | string},
) => ReactElement;

export const metricButtonStyles = (theme: Theme) => ({
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
    background: "none",
    border: "none",
    padding: 0,
    margin: 0,
    cursor: "pointer",
    font: "inherit",
    color: theme.palette.text.secondary,
    "&:hover": {color: theme.palette.text.primary},
    "&:disabled": {cursor: "default", color: theme.palette.text.secondary},
    "& svg": {fontSize: 16, width: 16, height: 16},
});

export const ProjectCardRoot = styled(Paper, {
    shouldForwardProp: (prop) =>
        prop !== "widthResponsive" &&
        prop !== "uniformHeight" &&
        prop !== "titleExpanded" &&
        prop !== "descriptionExpanded",
})<ProjectCardRootStyleProps>(({theme, widthResponsive, uniformHeight, titleExpanded, descriptionExpanded}) => {
    // The card has two layouts: a wide one with the thumbnail beside the content
    // and a compact one without it. On the profile page the switch is driven by
    // the viewport (MUI breakpoints + <Hidden xsDown>). When `widthResponsive` is
    // set (e.g. the copilot drawer, where the card is far narrower than the
    // viewport) the same layouts switch off the card's own width via a container
    // query instead. Only the trigger changes — the layouts are identical.
    const wideQuery = widthResponsive
        ? `@container project-card (min-width: ${PROJECT_CARD_WIDE_THRESHOLD}px)`
        : theme.breakpoints.up("sm");

    return {
        borderRadius: 8,
        border: `1px solid ${theme.palette.divider}`,
        overflow: "hidden",
        position: "relative",
        padding: "0px 16px",
        ...(uniformHeight && {
            boxSizing: "border-box",
            height: titleExpanded || descriptionExpanded ? "auto" : UNIFORM_PROJECT_CARD_HEIGHT_PX,
            minHeight: UNIFORM_PROJECT_CARD_HEIGHT_PX,
        }),
        [PROJECT_CARD_PREVIOUS_SIBLING_SELECTOR]: {marginTop: PROJECT_CARD_ROW_GAP_PX},
        [`&:hover .${classes.moreHorizIconButton}`]: {visibility: "visible"},
        ...(uniformHeight && {
            [`& .${classes.metricRow} .${classes.metricFullLabel}`]: {display: "none"},
            [`& .${classes.metricRow} .${classes.metricCompactLabel}`]: {display: "inline"},
        }),
        [wideQuery]: {
            padding: "0px 16px 0px 32px",
            ...(uniformHeight && {
                [`& .${classes.metricRow} .${classes.metricFullLabel}`]: {display: "inline"},
                [`& .${classes.metricRow} .${classes.metricCompactLabel}`]: {display: "none"},
            }),
        },
        [`&.${classes.cardWrapperSelectable}`]: {cursor: "pointer", userSelect: "none"},
        [`&.${classes.cardWrapperSelected}`]: {
            backgroundColor: alpha(theme.palette.primary.main, 0.08),
            borderColor: alpha(theme.palette.primary.main, 0.5),
        },
        [`& .${classes.selectionCheckbox}`]: {position: "absolute", top: 8, right: 8, zIndex: 1, padding: 4},
        [`& .${classes.cardInner}`]: {display: "flex", flexDirection: "row", gap: 32},
        [`& .${classes.thumbnailWrapper}`]: {
            display: "flex",
            alignItems: "center",
            flexShrink: 0,
            // The compact layout drops the thumbnail: below the wide threshold
            // in width-responsive mode, below the sm breakpoint otherwise.
            ...(widthResponsive
                ? {
                      [`@container project-card (max-width: ${PROJECT_CARD_WIDE_THRESHOLD - 1}px)`]: {
                          display: "none",
                      },
                  }
                : {[theme.breakpoints.down("sm")]: {display: "none"}}),
        },
        [`& .${classes.content}`]: {
            flex: 1,
            display: "flex",
            flexDirection: "column",
            gap: 16,
            padding: uniformHeight ? "18px 0px 8px 0px" : "10px 0px 8px 0px",
            minWidth: 0,
            [wideQuery]: {padding: "18px 0 8px 0px"},
        },
        [`& .${classes.topSection}`]: {
            display: "flex",
            flexDirection: "column",
            gap: 4,
            ...(uniformHeight && {
                height: titleExpanded || descriptionExpanded ? "auto" : 104,
                flexShrink: 0,
                overflow: titleExpanded || descriptionExpanded ? "visible" : "hidden",
            }),
        },
        // In uniform-height mode (the virtualized listing, where cards sit
        // alongside fixed-size skeletons) the name is clamped to one line and
        // the description always reserves two lines, so every card has the
        // same height regardless of its content.
        [`& .${classes.name}`]: uniformHeight
            ? {
                  whiteSpace: "normal",
                  overflow: titleExpanded ? "visible" : "hidden",
                  lineHeight: `${UNIFORM_PROJECT_CARD_TITLE_HEIGHT_PX}px`,
                  height: titleExpanded ? "auto" : UNIFORM_PROJECT_CARD_TITLE_HEIGHT_PX,
                  paddingRight: 20,
              }
            : {wordBreak: "break-word", lineHeight: 1.3, paddingRight: 20},
        [`& .${classes.description}`]: {
            wordBreak: "break-word",
            paddingBottom: 4,
            ...(uniformHeight && {
                boxSizing: "border-box",
                height: descriptionExpanded ? "auto" : UNIFORM_DESCRIPTION_HEIGHT_PX,
                minHeight: UNIFORM_DESCRIPTION_HEIGHT_PX,
                lineHeight: "20px",
                overflow: descriptionExpanded ? "visible" : "hidden",
            }),
        },
        [`& .${classes.authorRow}`]: {
            display: "flex",
            alignItems: "center",
            flexWrap: uniformHeight ? "nowrap" : "wrap",
            gap: 8,
            paddingTop: 2,
            ...(uniformHeight && {
                boxSizing: "border-box",
                height: UNIFORM_AUTHOR_HEIGHT_PX,
                minHeight: UNIFORM_AUTHOR_HEIGHT_PX,
                overflow: "hidden",
            }),
        },
        [`& .${classes.authorRowInner}`]: {
            display: uniformHeight ? "flex" : "contents",
            ...(uniformHeight && {
                flex: "0 1 auto",
                minWidth: 0,
                alignItems: "center",
                gap: 8,
                overflow: "hidden",
                whiteSpace: "nowrap",
            }),
        },
        [`& .${classes.authorRowTrailing}`]: {
            display: "flex",
            alignItems: "center",
            gap: 8,
            flexShrink: 0,
        },
        [`& .${classes.authorSeparator}`]: {display: "inline-flex", alignItems: "center"},
        [`& .${classes.privacyIcon}`]: {
            display: "inline-flex",
            alignItems: "center",
            "& svg": {color: theme.palette.text.secondary, fontSize: 16},
        },
        [`& .${classes.metricCompactLabel}`]: {display: "none"},
        [`& .${classes.starMetricButton}`]: metricButtonStyles(theme),
        [`& .${classes.moreHorizIconButton}`]: {
            flexShrink: 0,
            padding: 2,
            "@media (hover: hover)": {visibility: "hidden"},
            "& svg": {color: theme.palette.text.secondary},
        },
        [`& .${classes.metricRow}`]: {
            display: "flex",
            alignItems: "center",
            gap: 12,
            borderTop: `1px solid ${theme.palette.divider}`,
            paddingTop: 8,
            ...(uniformHeight && {
                boxSizing: "border-box",
                height: UNIFORM_METRIC_HEIGHT_PX,
                minHeight: UNIFORM_METRIC_HEIGHT_PX,
                overflow: "hidden",
            }),
        },
        [`& .${classes.metricRowInner}`]: {
            flex: 1,
            minWidth: 0,
            display: "flex",
            alignItems: "center",
            flexWrap: uniformHeight ? "nowrap" : "wrap",
            gap: "4px 12px",
            ...(uniformHeight && {
                height: 24,
                overflow: "hidden",
                "& > *": {height: 24, flexShrink: 0, whiteSpace: "nowrap"},
            }),
        },
        [`& .${classes.metricRowCollapsed}`]: {maxHeight: 24, overflow: "hidden", "& > *": {height: 24}},
        [`& .${classes.metricShowMore}`]: {...metricButtonStyles(theme), flexShrink: 0},
    };
}) as ProjectCardRootComponent;

export const ProjectCardSkeletonRoot = styled(ProjectCardRoot)(({theme}) => ({
    "& .MuiSkeleton-root": {
        backgroundColor: alpha(theme.palette.text.primary, 0.06),
    },
})) as ProjectCardRootComponent;

/**
 * Container-query wrapper enabling the card's `widthResponsive` layout switch.
 */
export const ProjectCardContainer: typeof Box = styled(Box)({
    containerType: "inline-size",
    containerName: "project-card",
    [PROJECT_CARD_PREVIOUS_SIBLING_SELECTOR]: {marginTop: PROJECT_CARD_ROW_GAP_PX},
}) as typeof Box;
